// src/components/ResetPassword.js

import React, { useState } from 'react';
import {
  Box,
  Button,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  FormControl,
  FormLabel,
  Text,
  useToast,
  VStack,
  HStack,
  Heading,
  Link,
} from '@chakra-ui/react';
import { LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { motion, isValidMotionProp } from 'framer-motion';
import { chakra } from '@chakra-ui/react';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase'; // Import the auth object

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) ||
    [
      'children',
      'width',
      'height',
      'bg',
      'bgGradient',
      'position',
      'zIndex',
      'top',
      'left',
      'right',
      'bottom',
      'display',
      'alignItems',
      'justifyContent',
      'p',
      'overflow',
      'rounded',
      'boxShadow',
      'maxW',
      'initial',
      'animate',
      'transition',
      'mb',
      'textAlign',
      'fontSize',
      'color',
      'textShadow',
      'onSubmit',
      'id',
      'isRequired',
      'type',
      'placeholder',
      'variant',
      'value',
      'onChange',
      'focusBorderColor',
      'aria-label',
      'icon',
      'onClick',
      'size',
      'colorScheme',
      '_hover',
    ].includes(prop),
});

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from query parameters
  const query = new URLSearchParams(location.search);
  const oobCode = query.get('oobCode'); // Firebase uses 'oobCode' as the reset token

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setPasswordError(false);
    setConfirmPasswordError(false);

    // Basic password validation
    if (password.length < 6) {
      setPasswordError(true);
      toast({
        title: 'Weak Password',
        description: 'Password must be at least 6 characters long.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (!oobCode) {
        throw new Error('Invalid or missing reset token.');
      }

      // Confirm the password reset with Firebase
      await confirmPasswordReset(auth, oobCode, password);

      toast({
        title: 'Password Reset Successful',
        description: 'You can now log in with your new password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/login');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: 'Error',
        description:
          error.code === 'auth/invalid-action-code'
            ? 'The password reset link is invalid or has expired.'
            : 'Failed to reset password. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-r, gray.700, gray.900)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p="4"
      position="relative"
      overflow="hidden"
    >
      {/* Overlay for better readability */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        bg="blackAlpha.600"
        zIndex="0"
      />

      {/* Reset Password Form */}
      <MotionBox
        bg="rgba(255, 255, 255, 0.1)"
        backdropFilter="blur(10px)"
        borderRadius="lg"
        boxShadow="2xl"
        zIndex="1"
        maxW="md"
        width="100%"
        p={['6', '8']}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <VStack spacing="6" align="stretch">
          {/* Logo and Title */}
          <Box textAlign="center">
            <Image
              src="/images/icononly.png" // Adjust the path as necessary
              alt="TrueMetric.io Logo"
              boxSize={['80px', '120px']}
              objectFit="contain"
              mx="auto"
            />
            <Text
              bgGradient="linear(to-r, white, blue.500, purple.400)"
              bgClip="text"
              fontSize={['xl', '2xl']}
              fontWeight="semibold"
              mt="2"
              textAlign="center"
            >
              TrueMetric.io
            </Text>
          </Box>

          <Heading
            as="h2"
            size="lg"
            textAlign="center"
            color="whiteAlpha.800"
          >
            Reset Your Password
          </Heading>
          <Text textAlign="center" color="gray.300" mb="6">
            Enter your new password below to reset it.
          </Text>

          <form onSubmit={handlePasswordReset}>
            <VStack spacing="4">
              <FormControl id="password" isRequired isInvalid={passwordError}>
                <FormLabel color="whiteAlpha.800">New Password</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <LockIcon color="orange.400" />
                  </InputLeftElement>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    variant="filled"
                    bg="whiteAlpha.300"
                    _hover={{ bg: 'whiteAlpha.400' }}
                    _focus={{ bg: 'whiteAlpha.400' }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    focusBorderColor="orange.400"
                    borderRadius="md"
                  />
                  <InputRightElement>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {passwordError && (
                  <Text color="red.500" fontSize="sm" mt="1">
                    Password must be at least 6 characters long.
                  </Text>
                )}
              </FormControl>

              <FormControl id="confirm-password" isRequired isInvalid={confirmPasswordError}>
                <FormLabel color="whiteAlpha.800">Confirm New Password</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <LockIcon color="orange.400" />
                  </InputLeftElement>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    variant="filled"
                    bg="whiteAlpha.300"
                    _hover={{ bg: 'whiteAlpha.400' }}
                    _focus={{ bg: 'whiteAlpha.400' }}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    focusBorderColor="orange.400"
                    borderRadius="md"
                  />
                  <InputRightElement>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {confirmPasswordError && (
                  <Text color="red.500" fontSize="sm" mt="1">
                    Passwords do not match.
                  </Text>
                )}
              </FormControl>

              <Button
                type="submit"
                colorScheme="orange"
                size="lg"
                width="full"
                isLoading={isSubmitting}
                _hover={{
                  bg: 'orange.500',
                  transform: 'scale(1.05)',
                }}
                leftIcon={<LockIcon />}
                transition="all 0.3s ease"
              >
                Reset Password
              </Button>
            </VStack>
          </form>

          <HStack spacing="2" justify="center" mt="4">
            <Text color="gray.300">Remember your password?</Text>
            <Link as={RouterLink} to="/login" color="orange.300" fontWeight="semibold">
              Log In
            </Link>
          </HStack>
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default ResetPassword;
